import { useState, useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { useStore, selectAgendaItems } from '@/utils/store';
import { CATEGORY_ICONS, CATEGORY_COLORS } from '@/utils/format';
import { useCurrency } from '@/hooks/useCurrency';
import AddAgendaItemModal from '@/components/shared/AddAgendaItemModal';
import PageHeader from '@/components/shared/PageHeader';
import Button from '@/components/ui/Button';
import {
  Plus,
  ChevronLeft,
  ChevronRight,
  List,
  CalendarDays,
  Check,
  Clock,
  Copy,
  Trash2,
  Pencil,
  Repeat,
} from 'lucide-react';
import {
  startOfMonth,
  endOfMonth,
  eachDayOfInterval,
  getDay,
  format,
  isToday,
  parseISO,
} from 'date-fns';
import { es, enUS } from 'date-fns/locale';

function formatMonthLabel(monthStr, locale) {
  const [y, m] = monthStr.split('-');
  const date = new Date(Number(y), Number(m) - 1);
  return date.toLocaleDateString(locale === 'es' ? 'es-CL' : 'en-US', { month: 'long', year: 'numeric' });
}

function shiftMonth(monthStr, delta) {
  const [y, m] = monthStr.split('-').map(Number);
  const date = new Date(y, m - 1 + delta);
  return date.toISOString().slice(0, 7);
}

const WEEKDAYS_ES = ['L', 'M', 'X', 'J', 'V', 'S', 'D'];
const WEEKDAYS_EN = ['M', 'T', 'W', 'T', 'F', 'S', 'S'];

export default function Agenda() {
  const { t, i18n } = useTranslation();
  const agendaItems = useStore(selectAgendaItems);
  const markAgendaItemPaid = useStore((s) => s.markAgendaItemPaid);
  const removeAgendaItem = useStore((s) => s.removeAgendaItem);
  const duplicateAgendaItem = useStore((s) => s.duplicateAgendaItem);
  const { format: formatCurr } = useCurrency();

  const [selectedMonth, setSelectedMonth] = useState(new Date().toISOString().slice(0, 7));
  const [view, setView] = useState('list');
  const [showModal, setShowModal] = useState(false);
  const [editingItem, setEditingItem] = useState(null);

  const filtered = useMemo(() => {
    return agendaItems
      .filter((item) => item.date.startsWith(selectedMonth))
      .sort((a, b) => a.date.localeCompare(b.date));
  }, [agendaItems, selectedMonth]);

  const pendingExpenses = useMemo(
    () => filtered.filter((i) => !i.paid && i.type === 'expense').reduce((s, i) => s + (i.amount || 0), 0),
    [filtered]
  );
  const pendingIncome = useMemo(
    () => filtered.filter((i) => !i.paid && i.type === 'income').reduce((s, i) => s + (i.amount || 0), 0),
    [filtered]
  );

  const openEdit = (item) => {
    setEditingItem(item);
    setShowModal(true);
  };

  const closeModal = () => {
    setShowModal(false);
    setEditingItem(null);
  };

  const handleDuplicate = (e, id) => {
    e.stopPropagation();
    const nextMonth = shiftMonth(selectedMonth, 1);
    duplicateAgendaItem(id, nextMonth);
  };

  const handleMarkPaid = (e, id) => {
    e.stopPropagation();
    markAgendaItemPaid(id);
  };

  const handleDelete = (e, id) => {
    e.stopPropagation();
    removeAgendaItem(id);
  };

  // Calendar data
  const calendarDays = useMemo(() => {
    const [y, m] = selectedMonth.split('-').map(Number);
    const start = startOfMonth(new Date(y, m - 1));
    const end = endOfMonth(new Date(y, m - 1));
    return eachDayOfInterval({ start, end });
  }, [selectedMonth]);

  const leadingBlanks = useMemo(() => {
    if (calendarDays.length === 0) return 0;
    return (getDay(calendarDays[0]) + 6) % 7;
  }, [calendarDays]);

  const itemsByDate = useMemo(() => {
    const map = {};
    filtered.forEach((item) => {
      if (!map[item.date]) map[item.date] = [];
      map[item.date].push(item);
    });
    return map;
  }, [filtered]);

  const weekdayLabels = i18n.language === 'es' ? WEEKDAYS_ES : WEEKDAYS_EN;
  const dateFnsLocale = i18n.language === 'es' ? es : enUS;

  return (
    <div className="space-y-6 animate-fade-in">
      <PageHeader
        title={t('agenda.title')}
        hint={t('common.hints.agenda')}
        action={
          <Button size="sm" onClick={() => setShowModal(true)}>
            <Plus size={16} className="mr-1" />
            {t('agenda.addNew')}
          </Button>
        }
      />

      {/* Month navigator */}
      <div className="flex items-center justify-between">
        <button
          onClick={() => setSelectedMonth(shiftMonth(selectedMonth, -1))}
          className="p-2 rounded-lg hover:bg-gray-100 dark:hover:bg-gray-800 transition-colors text-gray-500"
        >
          <ChevronLeft size={20} />
        </button>
        <span className="text-sm font-semibold text-gray-900 dark:text-white capitalize">
          {formatMonthLabel(selectedMonth, i18n.language)}
        </span>
        <button
          onClick={() => setSelectedMonth(shiftMonth(selectedMonth, 1))}
          className="p-2 rounded-lg hover:bg-gray-100 dark:hover:bg-gray-800 transition-colors text-gray-500"
        >
          <ChevronRight size={20} />
        </button>
      </div>

      {/* View toggle */}
      <div className="flex gap-1.5 p-1 bg-gray-100 dark:bg-gray-800 rounded-xl">
        <button
          onClick={() => setView('list')}
          className={`flex-1 flex items-center justify-center gap-1.5 py-2 rounded-lg text-xs font-medium transition-colors ${
            view === 'list' ? 'bg-white dark:bg-gray-700 text-gray-900 dark:text-white shadow-sm' : 'text-gray-500'
          }`}
        >
          <List size={14} />
          {t('agenda.listView')}
        </button>
        <button
          onClick={() => setView('calendar')}
          className={`flex-1 flex items-center justify-center gap-1.5 py-2 rounded-lg text-xs font-medium transition-colors ${
            view === 'calendar' ? 'bg-white dark:bg-gray-700 text-gray-900 dark:text-white shadow-sm' : 'text-gray-500'
          }`}
        >
          <CalendarDays size={14} />
          {t('agenda.calendarView')}
        </button>
      </div>

      {/* Pending summary */}
      {(pendingExpenses > 0 || pendingIncome > 0) && (
        <div className="grid grid-cols-2 gap-3">
          <div className="card !p-3">
            <p className="text-[10px] uppercase font-bold text-gray-400 dark:text-gray-500 tracking-wider">{t('agenda.pendingExpenses')}</p>
            <p className="text-lg font-bold text-danger-600 dark:text-danger-400">{formatCurr(pendingExpenses)}</p>
          </div>
          <div className="card !p-3">
            <p className="text-[10px] uppercase font-bold text-gray-400 dark:text-gray-500 tracking-wider">{t('agenda.pendingIncome')}</p>
            <p className="text-lg font-bold text-success-600 dark:text-success-400">{formatCurr(pendingIncome)}</p>
          </div>
        </div>
      )}

      {/* List View */}
      {view === 'list' && (
        <>
          {filtered.length > 0 ? (
            <div className="space-y-3">
              {filtered.map((item) => (
                <div
                  key={item.id}
                  className={`card hover:border-brand-300 dark:hover:border-brand-600 transition-colors cursor-pointer group border-l-4 ${
                    item.type === 'income'
                      ? 'border-l-success-500'
                      : 'border-l-danger-500'
                  }`}
                  onClick={() => openEdit(item)}
                >
                  <div className="flex items-center gap-3">
                    <div
                      className={`w-10 h-10 rounded-xl flex items-center justify-center text-xl shrink-0 ${
                        item.type === 'income'
                          ? 'bg-success-50 dark:bg-success-500/10'
                          : 'bg-danger-50 dark:bg-danger-500/10'
                      }`}
                    >
                      {item.icon || CATEGORY_ICONS[item.category] || '📅'}
                    </div>
                    <div className="flex-1 min-w-0">
                      <div className="flex items-center gap-2">
                        <h3 className="font-semibold text-sm text-gray-900 dark:text-white truncate">{item.title}</h3>
                        {item.recurrence !== 'none' && (
                          <span className="inline-flex items-center gap-0.5 text-[10px] px-1.5 py-0.5 rounded-full font-medium bg-brand-50 dark:bg-brand-500/10 text-brand-600 dark:text-brand-400">
                            <Repeat size={10} />
                            {item.recurrence === 'weekly' ? t('agenda.form.recurrenceWeekly') : t('agenda.form.recurrenceMonthly')}
                          </span>
                        )}
                        <span
                          className={`inline-flex items-center gap-0.5 text-[10px] px-1.5 py-0.5 rounded-full font-medium ${
                            item.paid
                              ? 'bg-success-50 dark:bg-success-500/10 text-success-600 dark:text-success-400'
                              : 'bg-warning-50 dark:bg-warning-500/10 text-warning-600 dark:text-warning-400'
                          }`}
                        >
                          {item.paid ? <Check size={10} /> : <Clock size={10} />}
                          {item.paid ? t('agenda.paid') : t('agenda.pending')}
                        </span>
                      </div>
                      <div className="flex items-center gap-2 mt-0.5">
                        <span className="text-xs text-gray-500 dark:text-gray-400">
                          {format(parseISO(item.date), 'dd MMM', { locale: dateFnsLocale })}
                        </span>
                        <span className="text-xs text-gray-400">·</span>
                        <span className="text-xs text-gray-500 dark:text-gray-400">
                          {t(`categories.${item.category}`)}
                        </span>
                      </div>
                    </div>
                    <div className="flex items-center gap-2">
                      {item.amount > 0 && (
                        <span className={`text-sm font-bold ${
                          item.type === 'expense'
                            ? 'text-danger-600 dark:text-danger-400'
                            : 'text-success-600 dark:text-success-400'
                        }`}>
                          {item.type === 'expense' ? '-' : '+'}{formatCurr(item.amount)}
                        </span>
                      )}
                      <div className="flex items-center gap-0.5 opacity-0 group-hover:opacity-100 transition-opacity">
                        {!item.paid && (
                          <button
                            onClick={(e) => handleMarkPaid(e, item.id)}
                            className="p-1.5 text-gray-400 hover:text-success-500 transition-colors"
                            title={t('agenda.markAsPaid')}
                          >
                            <Check size={15} />
                          </button>
                        )}
                        <button
                          onClick={(e) => handleDuplicate(e, item.id)}
                          className="p-1.5 text-gray-400 hover:text-brand-500 transition-colors"
                          title={t('agenda.duplicate')}
                        >
                          <Copy size={15} />
                        </button>
                        <button
                          onClick={(e) => handleDelete(e, item.id)}
                          className="p-1.5 text-gray-400 hover:text-danger-500 transition-colors"
                        >
                          <Trash2 size={15} />
                        </button>
                      </div>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          ) : (
            <div className="card text-center py-12">
              <div className="text-4xl mb-3">📅</div>
              <p className="text-sm text-gray-500 dark:text-gray-400 mb-4">{t('agenda.noItems')}</p>
              <button
                onClick={() => setShowModal(true)}
                className="inline-flex items-center gap-1.5 text-sm font-medium text-brand-600 dark:text-brand-400 hover:text-brand-700 dark:hover:text-brand-300 transition-colors"
              >
                <Plus size={16} />
                {t('agenda.addNew')}
              </button>
            </div>
          )}
        </>
      )}

      {/* Calendar View */}
      {view === 'calendar' && (
        <div className="card !p-3">
          {/* Weekday headers */}
          <div className="grid grid-cols-7 mb-2">
            {weekdayLabels.map((d, i) => (
              <div key={i} className="text-center text-[10px] font-bold uppercase text-gray-400 dark:text-gray-500 py-1">
                {d}
              </div>
            ))}
          </div>
          {/* Days grid */}
          <div className="grid grid-cols-7 gap-px">
            {/* Leading blanks */}
            {Array.from({ length: leadingBlanks }).map((_, i) => (
              <div key={`blank-${i}`} className="min-h-[72px]" />
            ))}
            {/* Actual days */}
            {calendarDays.map((day) => {
              const dateStr = format(day, 'yyyy-MM-dd');
              const dayItems = itemsByDate[dateStr] || [];
              const today = isToday(day);
              return (
                <div
                  key={dateStr}
                  className={`min-h-[72px] p-1 rounded-lg border transition-colors ${
                    today
                      ? 'bg-brand-50 dark:bg-brand-500/10 border-brand-200 dark:border-brand-500/30'
                      : 'border-transparent hover:bg-gray-50 dark:hover:bg-gray-800/50'
                  }`}
                >
                  <div className={`text-[11px] font-medium mb-0.5 ${
                    today ? 'text-brand-600 dark:text-brand-400 font-bold' : 'text-gray-500 dark:text-gray-400'
                  }`}>
                    {format(day, 'd')}
                  </div>
                  <div className="space-y-0.5">
                    {dayItems.slice(0, 3).map((item) => (
                      <button
                        key={item.id}
                        onClick={() => openEdit(item)}
                        className={`w-full text-left text-[9px] px-1 py-0.5 rounded truncate font-medium transition-opacity hover:opacity-80 ${
                          item.paid ? 'opacity-50 line-through' : ''
                        } ${
                          item.type === 'income'
                            ? 'bg-success-50 dark:bg-success-500/15 text-success-700 dark:text-success-400'
                            : 'bg-danger-50 dark:bg-danger-500/15 text-danger-700 dark:text-danger-400'
                        }`}
                        title={`${item.type === 'income' ? '+' : '-'} ${item.title}`}
                      >
                        {item.type === 'income' ? '+' : '-'} {item.title}
                      </button>
                    ))}
                    {dayItems.length > 3 && (
                      <div className="text-[9px] text-gray-400 text-center">+{dayItems.length - 3}</div>
                    )}
                  </div>
                </div>
              );
            })}
          </div>
        </div>
      )}

      <AddAgendaItemModal open={showModal} onClose={closeModal} item={editingItem} />
    </div>
  );
}
